import { useEffect, useState } from "react";
import { ShieldCheck, KeyRound, Lock } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { api, setup2FA, verify2FA, disable2FA, adminMe } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminSettings() {
  const { toast } = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [twofaPending, setTwofaPending] = useState(null);
  const [code, setCode] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    adminMe().then((d) => {
      setEmail(d.email);
      setEnabled(!!d.totp_enabled);
    }).catch(() => {});
  }, []);

  async function changePassword() {
    if (next !== next2) {
      toast({ title: "Wachtwoorden komen niet overeen", variant: "destructive" });
      return;
    }
    if (next.length < 10) {
      toast({ title: "Wachtwoord moet minstens 10 tekens hebben", variant: "destructive" });
      return;
    }
    try {
      await api.post("/admin/auth/change-password", { current_password: current, new_password: next });
      toast({ title: "Wachtwoord gewijzigd" });
      setCurrent(""); setNext(""); setNext2("");
    } catch (err) {
      toast({ title: "Fout", description: err?.response?.data?.detail || "Probeer opnieuw", variant: "destructive" });
    }
  }

  async function start2FA() {
    try {
      const d = await setup2FA();
      setTwofaPending(d);
    } catch (err) {
      toast({ title: "Fout", description: err?.response?.data?.detail || "Probeer opnieuw", variant: "destructive" });
    }
  }

  async function confirm2FA() {
    try {
      await verify2FA(code);
      toast({ title: "2FA actief", description: "Vanaf nu vraagt CheckExpert je TOTP-code bij login (toekomstige feature)." });
      setEnabled(true);
      setTwofaPending(null);
      setCode("");
    } catch (err) {
      toast({ title: "Code klopt niet", variant: "destructive" });
    }
  }

  async function turnOff2FA() {
    if (!window.confirm("2FA uitschakelen?")) return;
    await disable2FA();
    setEnabled(false);
    toast({ title: "2FA uitgeschakeld" });
  }

  const qrUrl = twofaPending ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twofaPending.otpauth_uri)}` : null;

  return (
    <AdminLayout>
      <div data-testid="admin-settings-page" className="space-y-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Account &amp; beveiliging</h1>
          <p className="text-ink-soft mt-1 text-sm">{email}</p>
        </div>

        <section className="rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#002FA7]" /> Wachtwoord wijzigen
          </h2>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <div>
              <Label>Huidig wachtwoord</Label>
              <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pw-current" />
            </div>
            <div>
              <Label>Nieuw wachtwoord</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="pw-next" />
            </div>
            <div>
              <Label>Bevestig nieuw</Label>
              <Input type="password" value={next2} onChange={(e) => setNext2(e.target.value)} data-testid="pw-next2" />
            </div>
          </div>
          <p className="text-xs text-ink-soft mt-3">Minstens 10 tekens. Gebruik een mix van letters, cijfers en symbolen.</p>
          <button onClick={changePassword} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold" data-testid="change-pw">
            Wachtwoord opslaan
          </button>
        </section>

        <section className="rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" /> Tweestapsverificatie (TOTP)
          </h2>
          <p className="text-ink-soft text-sm mt-2">
            Voeg een extra beveiligingslaag toe met Google Authenticator, Authy of 1Password.
          </p>

          {enabled ? (
            <div className="mt-4 flex items-center gap-3">
              <span className="text-xs uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">
                Actief
              </span>
              <button onClick={turnOff2FA} className="text-sm text-red-600 font-semibold hover:underline" data-testid="disable-2fa">
                Uitschakelen
              </button>
            </div>
          ) : !twofaPending ? (
            <button onClick={start2FA} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink hover:bg-ink-soft text-white text-sm font-semibold" data-testid="setup-2fa">
              <Lock className="h-4 w-4" /> 2FA opzetten
            </button>
          ) : (
            <div className="mt-4 grid sm:grid-cols-2 gap-6 items-start">
              <div>
                <p className="text-sm text-ink">1. Scan deze QR met je authenticator-app:</p>
                <img src={qrUrl} alt="2FA QR code" className="mt-3 rounded-xl border border-border" />
                <p className="text-xs text-ink-soft mt-2 break-all">Of voer secret in: <code>{twofaPending.secret}</code></p>
              </div>
              <div>
                <p className="text-sm text-ink mb-2">2. Voer de 6-cijferige code in:</p>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} data-testid="2fa-code" />
                <button onClick={confirm2FA} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid="verify-2fa">
                  Activeer
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
